import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Container, Badge } from "@mui/material";
import { useTranslation } from "react-i18next";
import ReimbursementV2ProcessingPage from "./ReimbursementV2Processing";
import InvoiceV2ProcessingPage from "./InvoiceV2Processing";
import ReceiptIcon from "@mui/icons-material/Receipt";
import reimbursementV2Api from "../services/reimbursement-v2-api";
import invoiceV2Api from "../services/invoice-v2-api";
import { useBadgeRefresh } from "../contexts/BadgeRefreshContext";

/**
 * Combined Finance Admin Desk Page with tabs for processing
 * - Reimbursement V2 Processing: Admin page for processing reimbursement requests
 * - Invoice V2 Processing: Admin page for processing invoice requests
 */
const FinanceAdminDesk: React.FC = () => {
  const { t } = useTranslation();
  const { triggerRefresh } = useBadgeRefresh();
  const [activeTab, setActiveTab] = useState(0);
  const [reimbursementPendingCount, setReimbursementPendingCount] = useState(0);
  const [invoicePendingCount, setInvoicePendingCount] = useState(0);
  const [localRefreshTrigger, setLocalRefreshTrigger] = useState(0);

  // Fetch pending counts
  useEffect(() => {
    const fetchPendingCounts = async () => {
      try {
        const [reimbursementCount, invoiceCount] = await Promise.all([
          reimbursementV2Api.getPendingRequestsCount(),
          invoiceV2Api.getPendingRequestsCount(),
        ]);
        setReimbursementPendingCount(reimbursementCount);
        setInvoicePendingCount(invoiceCount);
      } catch (error) {
        console.error("Failed to fetch pending counts:", error);
      }
    };

    fetchPendingCounts();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingCounts, 30000);

    return () => clearInterval(interval);
  }, [localRefreshTrigger]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Function to trigger badge refresh (can be called after status changes)
  const refreshBadges = () => {
    setLocalRefreshTrigger((prev) => prev + 1);
    triggerRefresh(); // Also trigger refresh in Header component
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="finance admin desk tabs"
          variant="fullWidth"
          indicatorColor="secondary"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tab
            label={t("reimbursement.v2.processing.title")}
            id="tab-reimbursement-processing"
            aria-controls="tabpanel-reimbursement-processing"
            icon={
              <Badge
                badgeContent={reimbursementPendingCount}
                color="error"
                max={99}
              >
                <ReceiptIcon />
              </Badge>
            }
            iconPosition="top"
          />
          <Tab
            label={t("invoice.v2.processing")}
            id="tab-invoice-processing"
            aria-controls="tabpanel-invoice-processing"
            icon={
              <Badge badgeContent={invoicePendingCount} color="error" max={99}>
                <ReceiptIcon />
              </Badge>
            }
            iconPosition="top"
          />
        </Tabs>
      </Box>

      {/* Reimbursement Processing Tab Panel */}
      <Box
        role="tabpanel"
        hidden={activeTab !== 0}
        id="tabpanel-reimbursement-processing"
        aria-labelledby="tab-reimbursement-processing"
      >
        {activeTab === 0 && (
          <ReimbursementV2ProcessingPage onStatusChange={refreshBadges} />
        )}
      </Box>

      {/* Invoice Processing Tab Panel */}
      <Box
        role="tabpanel"
        hidden={activeTab !== 1}
        id="tabpanel-invoice-processing"
        aria-labelledby="tab-invoice-processing"
      >
        {activeTab === 1 && (
          <InvoiceV2ProcessingPage onStatusChange={refreshBadges} />
        )}
      </Box>
    </Container>
  );
};

export default FinanceAdminDesk;
